import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, LayoutGrid, ChevronDown, Check } from "lucide-react";
import { CITIES } from "../mock/mockData";
import { citySlug } from "../lib/seo";
import { useCategories } from "../context/CategoriesContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

const SearchBar = ({ variant = "hero" }) => {
  const { categories: CATEGORIES } = useCategories();
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = () => {
    const slug = category || "dugun-salonlari";
    if (city) { navigate(`/${slug}/${citySlug(city)}`); return; }
    navigate(`/kategori/${slug}`);
  };

  const isHero = variant === "hero";
  const selectedCat = CATEGORIES.find((c) => c.slug === category);
  const mekanCats = CATEGORIES.filter((c) => c.group === "mekan");
  const firmaCats = CATEGORIES.filter((c) => c.group === "firma");

  return (
    <div
      className={`w-full ${
        isHero
          ? "bg-white/95 backdrop-blur-md shadow-[0_24px_60px_-20px_rgba(0,0,0,0.4)]"
          : "bg-white shadow-md border border-[#F0DDE0]"
      } rounded-2xl p-3 md:p-4`}
    >
      <div className="flex flex-col md:flex-row gap-3">
        <Popover open={catOpen} onOpenChange={setCatOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="category-combobox-trigger"
              className="flex-1 flex items-center gap-2 px-3 h-12 rounded-xl bg-[#FBF5F0] border border-transparent hover:border-[#E7A9B0] transition-colors text-left text-[#2A2224] outline-none"
            >
              <LayoutGrid className="w-5 h-5 text-[#C15B6B] shrink-0" />
              <span className={`flex-1 ${selectedCat ? "" : "text-[#8a7d7f]"}`}>{selectedCat ? selectedCat.name : "Kategori Seç"}</span>
              <ChevronDown className="w-4 h-4 text-[#8a7d7f] shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={8} className="p-0 w-[min(92vw,380px)]" data-testid="category-combobox-content">
            <Command>
              <CommandInput placeholder="Kategori ara..." data-testid="category-search-input" />
              <CommandList className="max-h-80">
                <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                <CommandGroup heading="Düğün Mekanları">
                  {mekanCats.map((c) => (
                    <CommandItem
                      key={c.slug}
                      value={c.name}
                      data-testid={`category-option-${c.slug}`}
                      onSelect={() => { setCategory(c.slug); setCatOpen(false); }}
                      className="cursor-pointer"
                    >
                      <Check className={`mr-2 h-4 w-4 ${category === c.slug ? "opacity-100 text-[#9E2A3B]" : "opacity-0"}`} />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandGroup heading="Düğün Firmaları">
                  {firmaCats.map((c) => (
                    <CommandItem
                      key={c.slug}
                      value={c.name}
                      data-testid={`category-option-${c.slug}`}
                      onSelect={() => { setCategory(c.slug); setCatOpen(false); }}
                      className="cursor-pointer"
                    >
                      <Check className={`mr-2 h-4 w-4 ${category === c.slug ? "opacity-100 text-[#9E2A3B]" : "opacity-0"}`} />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Popover open={cityOpen} onOpenChange={setCityOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="city-combobox-trigger"
              className="flex-1 flex items-center gap-2 px-3 h-12 rounded-xl bg-[#FBF5F0] border border-transparent hover:border-[#E7A9B0] transition-colors text-left text-[#2A2224] outline-none"
            >
              <MapPin className="w-5 h-5 text-[#C15B6B] shrink-0" />
              <span className={`flex-1 ${city ? "" : "text-[#8a7d7f]"}`}>{city || "Şehir Seç"}</span>
              <ChevronDown className="w-4 h-4 text-[#8a7d7f] shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={8} className="p-0 w-[min(92vw,320px)]" data-testid="city-combobox-content">
            <Command>
              <CommandInput placeholder="Şehir ara..." data-testid="city-search-input" />
              <CommandList className="max-h-80">
                <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                <CommandGroup>
                  {CITIES.map((c) => (
                    <CommandItem
                      key={c}
                      value={c}
                      data-testid={`city-option-${c}`}
                      onSelect={() => { setCity(c); setCityOpen(false); }}
                      className="cursor-pointer"
                    >
                      <Check className={`mr-2 h-4 w-4 ${city === c ? "opacity-100 text-[#9E2A3B]" : "opacity-0"}`} />
                      {c}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <button
          onClick={handleSearch}
          data-testid="searchbar-submit"
          className="flex items-center justify-center gap-2 px-8 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
          <Search className="w-5 h-5" />
          Bul
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
